export const BUBBLE_TRAP_MS = 6_000;
export const RESCUE_MS = 8_000;
export const BUBBLE_COOLDOWN_MS = 350;
export const MAX_PLAYER_BUBBLES = 3;
export const BOSS_STAGGER_HITS = 3;
export const BOSS_TRAP_MS = 5_000;

export type BubblePhase = 'flight' | 'floating' | 'trapped' | 'expired';

export function bubblePhase(ageMs: number, trappedAtMs?: number): BubblePhase {
  if (trappedAtMs !== undefined) {
    return ageMs - trappedAtMs >= BUBBLE_TRAP_MS ? 'expired' : 'trapped';
  }
  if (ageMs < 520) return 'flight';
  if (ageMs < 4_200) return 'floating';
  return 'expired';
}

export class ScoreChain {
  score: number;
  multiplier = 1;
  private lastPopAt = Number.NEGATIVE_INFINITY;

  constructor(initialScore = 0) {
    this.score = initialScore;
  }

  capture(): number {
    this.score += 100;
    return 100;
  }

  pop(now: number): number {
    this.multiplier = now - this.lastPopAt <= 1_200 ? Math.min(5, this.multiplier + 1) : 1;
    this.lastPopAt = now;
    const gained = 500 * this.multiplier;
    this.score += gained;
    return gained;
  }

  resetMultiplier(): void {
    this.multiplier = 1;
    this.lastPopAt = Number.NEGATIVE_INFINITY;
  }
}

export interface DamageResult {
  hearts: number;
  state: 'hurt' | 'rescue' | 'out';
}

export function applyDamage(hearts: number, coop: boolean, rescueUsed: boolean): DamageResult {
  const nextHearts = Math.max(0, hearts - 1);
  if (nextHearts > 0) return { hearts: nextHearts, state: 'hurt' };
  if (coop && !rescueUsed) return { hearts: 0, state: 'rescue' };
  return { hearts: 0, state: 'out' };
}

export function canRescue(startedAt: number, now: number): boolean {
  return now - startedAt < RESCUE_MS;
}

export interface BossBubbleProgress {
  hits: number;
  result: 'stagger' | 'trap';
}

/** Three bubbles weaken the boss; the next one seals it inside a bubble. */
export function registerBossBubbleHit(currentHits: number): BossBubbleProgress {
  const safeHits = Math.max(0, Math.min(BOSS_STAGGER_HITS, Math.floor(currentHits)));
  if (safeHits >= BOSS_STAGGER_HITS) return { hits: safeHits, result: 'trap' };
  return { hits: safeHits + 1, result: 'stagger' };
}

export type HorizontalDirection = -1 | 1;

/** Keeps an actor moving back into the playable area even if its AI still points at a wall. */
export function safeHorizontalDirection(
  x: number,
  requested: HorizontalDirection,
  blockedLeft: boolean,
  blockedRight: boolean,
  minX = 24,
  maxX = 456,
): HorizontalDirection {
  if (blockedLeft || x <= minX) return 1;
  if (blockedRight || x >= maxX) return -1;
  return requested;
}

export function madeHorizontalProgress(previousX: number, currentX: number, minimumDistance = 4): boolean {
  return Math.abs(currentX - previousX) >= minimumDistance;
}
